import styled, { keyframes } from "styled-components";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(20px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;
`;

export const ModalContainer = styled.div`
  background: ${({ theme }) => theme.card || '#1a1c23'};
  border: 1px solid ${({ theme }) => theme.border || 'rgba(255,255,255,0.08)'};
  border-radius: 16px;
  width: 100%;
  max-width: ${({ $maxWidth }) => $maxWidth || '900px'};
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: ${slideUp} 0.3s ease;

  @media (max-width: 768px) {
    max-width: 95%;
    max-height: 90vh;
  }
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  border-bottom: 1px solid ${({ theme }) => theme.border || 'rgba(255,255,255,0.06)'};
  flex-shrink: 0;

  .header-left {
    display: flex;
    align-items: center;
    gap: 10px;

    .header-icon {
      width: 32px;
      height: 32px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      background: rgba(16, 185, 129, 0.1);
      color: #10b981;
      font-size: 1em;

      .spin {
        animation: headerSpin 1s linear infinite;
      }
    }

    @keyframes headerSpin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    h2 {
      margin: 0;
      font-size: 0.95em;
      font-weight: 500;
      color: ${({ theme }) => theme.text || '#fff'};
      opacity: 0.85;
      letter-spacing: -0.01em;
    }

    p {
      margin: 0;
      font-size: 0.78em;
      color: ${({ theme }) => theme.text || '#fff'};
      opacity: 0.45;
    }
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .close-button {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    border: none;
    background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.04)' : 'rgba(255,255,255,0.04)'};
    color: ${({ theme }) => theme.text || '#fff'};
    opacity: 0.5;
    cursor: pointer;
    transition: all 0.2s ease;
    font-size: 1.1em;

    &:hover {
      opacity: 1;
      background: ${({ theme }) => theme.body === '#fafbfc' ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'};
    }
  }
`;

export const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
  min-height: 0;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.border || 'rgba(255,255,255,0.08)'};
    border-radius: 3px;
  }
`;

export const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $justify }) => $justify || 'flex-end'};
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid ${({ theme }) => theme.border || 'rgba(255,255,255,0.06)'};
  flex-shrink: 0;
`;

export const ModalButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 10px;
  font-size: 0.85em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  font-family: 'Inter', system-ui, sans-serif;

  &.primary {
    background: #10b981;
    color: white;

    &:hover:not(:disabled) {
      background: #059669;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background: transparent;
    border: 1px solid ${({ theme }) => theme.border || 'rgba(255,255,255,0.1)'};
    color: ${({ theme }) => theme.text || '#fff'};
    opacity: 0.7;

    &:hover {
      opacity: 1;
      border-color: ${({ theme }) => theme.text || 'rgba(255,255,255,0.3)'};
    }
  }

  &.danger {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.2);
    color: #ef4444;

    &:hover {
      background: rgba(239, 68, 68, 0.15);
      border-color: rgba(239, 68, 68, 0.4);
    }
  }

  &.info {
    background: rgba(59, 130, 246, 0.1);
    border: 1px solid rgba(59, 130, 246, 0.2);
    color: #3b82f6;

    &:hover {
      background: rgba(59, 130, 246, 0.15);
      border-color: rgba(59, 130, 246, 0.4);
    }
  }

  svg {
    font-size: 1em;
  }
`;
